import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Sparkles, Calendar, Gift } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EvenementsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_type: 'atelier',
    event_date: '',
    guests: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const eventTypes = [
    {
      id: 'atelier',
      icon: <Sparkles size={28} />,
      title: 'Atelier olfactif',
      description: "Une immersion guidée dans l'univers des fragrances, en groupe ou en individuel. Idéal pour découvrir, apprendre et créer.",
    },
    {
      id: 'animation',
      icon: <Users size={28} />,
      title: 'Animation itinérante (chez vous)',
      description: "Notre bar à parfums se déplace dans le lieu que vous réservez (domicile, salle, restaurant, hôtel…). Une expérience originale pour vos EVJF, anniversaires, fêtes entre amies ou célébrations. Vous choisissez le lieu, nous apportons l'ambiance.",
    },
    {
      id: 'evenement_prive',
      icon: <Calendar size={28} />,
      title: 'Événement privé sur votre lieu',
      description: "Soirée unique, animation d'entreprise, moment entre proches : nous nous déplaçons avec notre bar à parfums itinérant dans le lieu que vous réservez (domicile, salle, restaurant, hôtel, bureau…). Vous choisissez le cadre, nous apportons l'expérience.",
    },
    {
      id: 'partenariat',
      icon: <Users size={28} />,
      title: 'Partenariat & collaboration',
      description: "Marque, hôtel, restaurant, agence événementielle, comité d'entreprise... Construisons ensemble un partenariat sur-mesure autour du parfum.",
    },
    {
      id: 'devis',
      icon: <Gift size={28} />,
      title: 'Devis sur-mesure',
      description: "Comité d'entreprise, cadeaux corporate, mariages, partenariats... Demandez un devis personnalisé.",
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/events`, formData);
      toast.success('Demande envoyée ! Nous vous recontactons sous 48h.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        event_type: 'atelier',
        event_date: '',
        guests: '',
        message: '',
      });
    } catch (error) {
      toast.error("Erreur lors de l'envoi de votre demande");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFAF4] pb-16">
      {/* Hero with background image */}
      <section className="relative py-32 px-6 overflow-hidden border-b border-[#B8922A]/20" data-testid="evenements-hero">
        <img
          src="/assets/bar-ambulant-soiree.jpg"
          alt="Bar à parfums ambulant Harmony — soirées privées & événements"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#332724]/85 via-[#332724]/60 to-[#332724]/40 z-10" />
        <div className="max-w-4xl mx-auto text-center relative z-20 text-[#FDFAF4]">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
            Ateliers · Animations itinérantes · Événements privés (chez vous)
          </p>
          <h1
            className="text-5xl md:text-6xl font-light mb-6 text-[#FDFAF4]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            data-testid="evenements-title"
          >
            Vivez l'expérience
            <br />Harmony autrement
          </h1>
          <p className="text-lg text-[#FDFAF4]/90 font-light leading-relaxed mb-6">
            Réservez un atelier olfactif, organisez une animation pour vos proches ou
            privatisez notre bar à parfums pour un événement inoubliable. Chaque expérience
            est composée sur-mesure.
          </p>
          <p className="text-base text-[#FDFAF4]/85 font-light italic">
            EVJF · Mariages · Anniversaires · Baby showers · Séminaires d'entreprise · Soirées privées
          </p>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2
            className="text-3xl md:text-4xl font-light text-center mb-4 text-[#5C2018]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            Nos formules
          </h2>
          <p className="text-center text-[#332724] font-light mb-16 max-w-2xl mx-auto">
            Chaque demande est unique. Voici les principales expériences que nous proposons.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {eventTypes.map((type) => (
              <div
                key={type.id}
                className="bg-white p-8 rounded-lg border border-gold shadow-[0_8px_32px_rgba(92, 32, 24,0.05)] flex gap-5"
                data-testid={`event-type-${type.id}`}
              >
                <div className="w-14 h-14 bg-[#1E7A6A]/10 rounded-full flex items-center justify-center flex-shrink-0 text-[#1E7A6A]">
                  {type.icon}
                </div>
                <div>
                  <h3
                    className="text-2xl mb-2 text-[#5C2018]"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
                  >
                    {type.title}
                  </h3>
                  <p className="text-[#332724] font-light leading-relaxed">
                    {type.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section dédiée Partenariats & Collaborations entreprises */}
      <section className="py-24 px-6 bg-[#332724] text-[#FDFAF4]" data-testid="evenements-partenariats">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
                Vous êtes une entreprise ?
              </p>
              <h2
                className="text-4xl md:text-5xl font-light mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                Partenariats &
                <br />collaborations
                <br />d'entreprise
              </h2>
              <p className="text-lg font-light leading-relaxed mb-6 text-[#FDFAF4]/90">
                Hôtels, restaurants, agences événementielles, marques, comités d'entreprise,
                cabinets d'avocats, instituts… Nous construisons avec vous une
                <strong> collaboration sur-mesure</strong> autour du parfum.
              </p>
              <ul className="space-y-3 mb-8 text-[#FDFAF4]/85">
                <li className="flex gap-3">
                  <span className="text-[#B8922A] flex-shrink-0">◆</span>
                  <span>Cadeaux corporate & coffrets personnalisés à votre marque</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#B8922A] flex-shrink-0">◆</span>
                  <span>Ateliers DIY pour vos séminaires, team-buildings et incentives</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#B8922A] flex-shrink-0">◆</span>
                  <span>Bar à parfums ambulant pour vos inaugurations & salons</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#B8922A] flex-shrink-0">◆</span>
                  <span>Co-branding, création de fragrance signature pour votre marque</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#B8922A] flex-shrink-0">◆</span>
                  <span>Partenariats hôteliers, conciergeries, événementiel haut de gamme</span>
                </li>
              </ul>
              <div className="flex flex-wrap gap-4">
                <a
                  href="/assets/plaquette-harmony-entreprises.pdf"
                  download="Plaquette-Harmony-Entreprises.pdf"
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-[#B8922A] hover:bg-[#9a7922] text-[#FDFAF4] uppercase tracking-[0.2em] text-sm transition-colors"
                  data-testid="evenements-cta-plaquette"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Télécharger la plaquette
                </a>
                <a
                  href="mailto:contact@maisonharmony.com?subject=Demande%20de%20partenariat%20-%20Harmony%20Parfumerie"
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-full border border-[#FDFAF4]/30 hover:border-[#B8922A] text-[#FDFAF4] uppercase tracking-[0.2em] text-sm transition-colors"
                  data-testid="evenements-cta-email"
                >
                  Nous écrire
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#FDFAF4]/5 backdrop-blur-sm border border-[#FDFAF4]/15 rounded-2xl p-6 flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-[#B8922A]/20 rounded-full flex items-center justify-center mb-3">
                  <span className="text-[#B8922A] text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>H</span>
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">Hôtellerie</p>
                <p className="text-sm text-[#FDFAF4]/80 font-light">Welcome gift, signature olfactive lobby</p>
              </div>
              <div className="bg-[#FDFAF4]/5 backdrop-blur-sm border border-[#FDFAF4]/15 rounded-2xl p-6 flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-[#1E7A6A]/20 rounded-full flex items-center justify-center mb-3">
                  <span className="text-[#1E7A6A] text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>É</span>
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#1E7A6A] mb-1">Événementiel</p>
                <p className="text-sm text-[#FDFAF4]/80 font-light">Privatisation & animations clés en main</p>
              </div>
              <div className="bg-[#FDFAF4]/5 backdrop-blur-sm border border-[#FDFAF4]/15 rounded-2xl p-6 flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-[#5C2018]/40 rounded-full flex items-center justify-center mb-3">
                  <span className="text-[#FDFAF4] text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>C</span>
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">CSE & corporate</p>
                <p className="text-sm text-[#FDFAF4]/80 font-light">Cadeaux d'affaires, fin d'année, fidélisation</p>
              </div>
              <div className="bg-[#FDFAF4]/5 backdrop-blur-sm border border-[#FDFAF4]/15 rounded-2xl p-6 flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-[#B8922A]/20 rounded-full flex items-center justify-center mb-3">
                  <span className="text-[#B8922A] text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>M</span>
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-1">Marques & créateurs</p>
                <p className="text-sm text-[#FDFAF4]/80 font-light">Co-branding & fragrance sur-mesure</p>
              </div>
            </div>
          </div>

          <p className="text-center text-[#FDFAF4]/60 text-sm italic mt-12 max-w-2xl mx-auto">
            « Chaque partenariat est unique. Nous prenons le temps d'écouter, de comprendre,
            et de composer la collaboration qui vous ressemble. »
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Photo bar itinérant décoré */}
          <div className="relative rounded-2xl overflow-hidden mb-10 shadow-[0_20px_60px_rgba(92, 32, 24,0.15)]" data-testid="bar-itinerant-photo">
            <img
              src="/assets/bar-ambulant-decore.jpg"
              alt="Bar à parfums itinérant Harmony Parfumerie — événements privés et professionnels"
              className="w-full h-auto object-cover aspect-[21/9]"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#332724]/95 via-[#332724]/40 to-transparent p-6 md:p-8">
              <p
                className="text-[#FDFAF4] text-lg md:text-2xl font-light italic"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                « Notre bar à parfums se déplace pour sublimer vos plus beaux moments. »
              </p>
            </div>
          </div>

          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
              Faites-nous part de votre projet
            </p>
            <h2
              className="text-3xl md:text-4xl font-light text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Demande de réservation ou de devis
            </h2>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white p-8 rounded-lg border border-gold shadow-[0_8px_32px_rgba(92, 32, 24,0.05)]"
            data-testid="event-form"
          >
            <div className="mb-6">
              <label className="block text-sm uppercase tracking-[0.2em] mb-3 text-[#332724]">
                Type de demande *
              </label>
              <div className="grid grid-cols-2 gap-3">
                {eventTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, event_type: type.id })}
                    className={`p-3 rounded-lg border text-sm transition-all ${
                      formData.event_type === type.id
                        ? 'bg-[#1E7A6A] text-[#FDFAF4] border-[#1E7A6A]'
                        : 'bg-white text-[#332724] border-[#B8922A]/20 hover:border-[#1E7A6A]'
                    }`}
                  >
                    {type.title}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <input
                type="text"
                placeholder="Nom complet *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="p-3 border border-[#B8922A]/20 rounded-lg focus:outline-none focus:border-[#1E7A6A]"
                required
                data-testid="event-name"
              />
              <input
                type="email"
                placeholder="Email *"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="p-3 border border-[#B8922A]/20 rounded-lg focus:outline-none focus:border-[#1E7A6A]"
                required
                data-testid="event-email"
              />
            </div>
            <input
              type="tel"
              placeholder="Téléphone *"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full p-3 border border-[#B8922A]/20 rounded-lg focus:outline-none focus:border-[#1E7A6A] mb-4"
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#332724]">
                  Date envisagée
                </label>
                <input
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                  className="w-full p-3 border border-[#B8922A]/20 rounded-lg focus:outline-none focus:border-[#1E7A6A]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] mb-2 text-[#332724]">
                  Nombre de personnes
                </label>
                <input
                  type="text"
                  placeholder="Ex: 8 personnes"
                  value={formData.guests}
                  onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                  className="w-full p-3 border border-[#B8922A]/20 rounded-lg focus:outline-none focus:border-[#1E7A6A]"
                />
              </div>
            </div>

            <textarea
              placeholder="Décrivez-nous votre projet... *"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows="5"
              className="w-full p-3 border border-[#B8922A]/20 rounded-lg focus:outline-none focus:border-[#1E7A6A] mb-6"
              required
              data-testid="event-message"
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary px-8 py-4 rounded-full font-medium uppercase tracking-[0.2em] text-sm disabled:opacity-50"
              data-testid="event-submit"
            >
              {loading ? 'Envoi en cours...' : 'Envoyer ma demande'}
            </button>

            <p className="text-xs text-[#332724]/70 text-center mt-4">
              Réponse sous 48h. Nous vous contacterons pour affiner votre projet ensemble.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default EvenementsPage;
